import * as React from 'react';
import { useIntl } from 'react-intl';
import type { Locale } from '../intl/locales';
import { addScript } from '../scripts';
import type { Consent, SiteConsent } from './types';

declare global {
  interface Window {
    clarity: (arg: string) => void;
  }
}

function getOnload({ locale, onChange, onLoad }: InitConfig) {
  return function onload() {
    if (!window.WcpConsent) {
      throw new Error('Unable to load WcpConsent');
    }

    // oddly named variable, means that init was ALREADY called
    if (window.WcpConsent.isInitReady) {
      return;
    }

    const container = document.createElement('div');
    document.body.prepend(container);

    window.WcpConsent.init(
      locale,
      container,
      (err, consent) => {
        if (err) {
          // nothing we can do here
          return;
        }
        onLoad(consent!);
      },
      onChange
    );
  };
}

interface InitConfig {
  locale: Locale;
  onLoad: (consent: SiteConsent) => void;
  onChange: (newConsent: Consent) => void;
}

export const defaultConsent = {
  Required: true,
  Analytics: false,
  SocialMedia: false,
  Advertising: false,
};

interface Context {
  consent: Consent;
  initialized: boolean;
  siteConsent: SiteConsent | null;
}

const ConsentContext = React.createContext<Context>({ consent: defaultConsent, initialized: false, siteConsent: null });

interface Props {
  children: React.ReactNode;
}

export function CookieConsentContext({ children }: Props) {
  const intl = useIntl();
  const [consent, setConsent] = React.useState<Consent>(defaultConsent);
  const [siteConsent, setSiteConsent] = React.useState<SiteConsent | null>(null);
  const parentContext = React.useContext(ConsentContext);

  React.useEffect(() => {
    if (parentContext.initialized) {
      throw new Error('Cannot initialized WcpConsent more than once');
    }
    if (window.WcpConsent) {
      return;
    }
    addScript({
      src: 'https://wcpstatic.microsoft.com/mscc/lib/v2/wcp-consent.js',
      onload: getOnload({
        locale: intl.locale as Locale,
        onChange: (consent) => {
          setConsent({ ...consent });
          if (consent.Analytics) {
            window.clarity('consent');
          }
        },
        onLoad: (siteConsent) => {
          setConsent({ ...siteConsent.getConsent() });
          setSiteConsent(siteConsent);
          if (siteConsent.getConsentFor('Analytics')) {
            window.clarity('consent');
          }
        },
      }),
    });
  }, [parentContext]);

  return (
    <ConsentContext.Provider value={{ consent, initialized: true, siteConsent }}>{children}</ConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const consent = React.useContext(ConsentContext);
  if (!consent.initialized) {
    throw new Error('Must initialize Cookie Consent before reading');
  }

  return consent;
}
